// EventGroupCard — the board's visual grouping unit: one card per event.
// MatchHeader — the shared event header: logos + symbols + full names with
// the score centered, and a metadata row (sport, league, times, counts)
// supplied by the section.

import type { ReactNode } from 'react';
import { SportIcon } from '@mashtrade/ui';
import type { EventSnapshot } from '../feed';
import { TeamBadge } from './team-badge';

export function EventGroupCard({
  header,
  children,
}: {
  header: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl bg-content1 px-4 py-3">
      <div className="pb-1">{header}</div>
      <div className="flex flex-col divide-y divide-foreground/10">
        {children}
      </div>
    </div>
  );
}

/** Feed sports arrive mixed-case ("Soccer", "basketball") — SportIcon keys
 * are title-case. Normalize so both import batches map. */
export const sportTitle = (sport: string): string =>
  sport
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const symbolOf = (c: EventSnapshot['home']): string =>
  c.symbol || c.shortName || c.name;

export function MatchHeader({
  event,
  score,
  meta,
}: {
  event: EventSnapshot;
  /** null → no score yet (upcoming); undefined → "vs" placeholder */
  score?: string | null;
  meta?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <TeamBadge competitor={event.home} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight">
              {symbolOf(event.home)}
            </p>
            <p className="truncate text-xs leading-tight opacity-60">
              {event.home.name}
            </p>
          </div>
        </div>
        {score ? (
          <span className="rounded bg-black/20 px-2 py-0.5 text-sm font-semibold tabular-nums">
            {score}
          </span>
        ) : (
          <span className="text-xs opacity-50">vs</span>
        )}
        <div className="flex min-w-0 flex-row-reverse items-center gap-2 text-right">
          <TeamBadge competitor={event.away} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight">
              {symbolOf(event.away)}
            </p>
            <p className="truncate text-xs leading-tight opacity-60">
              {event.away.name}
            </p>
          </div>
        </div>
      </div>
      {meta && (
        <p className="flex items-center gap-1.5 truncate text-xs opacity-60">
          {meta}
        </p>
      )}
    </div>
  );
}

/** Shared meta row prefix: sport glyph + league. */
export function SportLeague({
  sport,
  league,
}: {
  sport: string;
  league: string;
}) {
  return (
    <>
      <SportIcon sport={sportTitle(sport)} />
      {(league || sport) && <span>{league || sport}</span>}
    </>
  );
}
